
import React, { useEffect } from "react";
import { useZooPassAppAnswersContext } from "../../Context/ZooPassAnswersContext";
import ZooPassFetch from "../../Utilities/ZooPassFetch";
import ZooPassStepDataUtility from "../../Utilities/ZooPassStepDataUtility";
import { useZooPassAppContext } from "../../Context/ZooPassAppContext";
import { LoadingSegment } from "./RenewalSupportingSegments";
import { MemberDataDto } from "../../Data/MemberDataDto";

export interface State {
    id: number;
    description: string;
    inactive?: boolean;
    stateCode: string;
}

export interface Country {
    id: number;
    description: string;
    inactive?: boolean;
    states: State[];
}

let debounceTimer: NodeJS.Timeout | null | undefined;

export default ({ override }: { override?: boolean}) => {
    const [formData, setFormData] = React.useState<MemberDataDto>({
        adultFName1: "",
        adultLName1: "",
        recipAddress: "",
        recipCity: "",
        recipState: "",
        recipPostal: "",
        recipPhone: "",
        recipEmail: "",
        giftPackageTo: "To the Recipient",
        giftRenewalTo: "To me",
        adultFName2: "",
        adultLName2: "",
        giftMessage: ""
    });

    const [validationMessages, setValidationMessages] = React.useState<{ [key: string]: string }>({});

    const [loading, setLoading] = React.useState<boolean>(true);

    
    let {
        dictionary
    } = useZooPassAppContext();

    
    const {
        answersData,
        answersLoading,
        updateContext
    } = useZooPassAppAnswersContext();

    const [countries, setCountries] = React.useState<Country[]>([]);

    useEffect(() => {
        ZooPassFetch.getCountries(window.baseApiUrl).then((countries) => {
            countries.sort((a, b) => a.id - b.id);
            setCountries(countries);
        });
    }, []);

    useEffect(() => {
        // If this is a logged in user, prefill the adult name fields
        if (answersData?.step1Value == 0) { 
            ZooPassFetch.fetchConstituentSnapshot(window.baseApiUrl).then((snapshot) => {
                if (snapshot) {
                    setFormData(prev => ({
                        ...prev,
                        adultFName1: snapshot.adult1FirstName || "",
                        adultLName1: snapshot.adult1LastName || "",
                        adultFName2: snapshot.adult2FirstName || "",
                        adultLName2: snapshot.adult2LastName || ""
                    }));

                    answersData.formData = {
                        ...answersData.formData,
                        adultFName1: answersData.formData.adultFName1 || snapshot.adult1FirstName || "",
                        adultLName1: answersData.formData.adultLName1 || snapshot.adult1LastName || "",
                        adultFName2: answersData.formData.adultFName2 || snapshot.adult2FirstName || "",
                        adultLName2: answersData.formData.adultLName2 || snapshot.adult2LastName || ""
                    };

                    if (override != true) {
                        if ((answersData.formData.adultFName2 ?? "" + answersData.formData.adultLName2) == "") {
                            answersData.step3Value = 1;
                        } else {
                            answersData.step3Value = 2;
                        }
                    }
                    updateContext({ type: "setAnswers", value: answersData });
                    setLoading(false);
                }
            });
        } else {
            setLoading(false);
        }
    }, [answersData?.step1Value]);

    useEffect(() => {
        if (!answersLoading && answersData != null && answersData.formData) {
            setFormData(answersData.formData);
        }   
    }, [answersLoading]);

    useEffect(() => {
        if (debounceTimer) {
            clearTimeout(debounceTimer);
        }
        debounceTimer = setTimeout(() => {
            if (validateForm())
                updateContext({ type: "setFormData", value: formData });
        }, 300);
        return () => {
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }
        };
    }, [formData, answersData?.step3Value]);

    const updateFormData = (data: MemberDataDto) => {
        setFormData(prev => {
            let updatedData = { ...prev, ...data };
            return updatedData;
        });
    }

    const validateForm = (): boolean => {
        let messages = ZooPassStepDataUtility.ValidateFormData(formData, answersData?.step3Value == 2 || override == true, answersData?.step1Value == 1);
        setValidationMessages(messages);
        return Object.keys(messages).length === 0;
    }

    const country = countries.find(c => c.description === "USA");

    const firstNameLabel = dictionary.get("ZooPass:FormData:FirstName") ?? "First Name";
    const lastNameLabel = dictionary.get("ZooPass:FormData:LastName") ?? "Last Name";

    const toMeLabel = dictionary.get("ZooPass:FormData:Gift:To:Me") ?? "To Me";
    const toRecipientLabel = dictionary.get("ZooPass:FormData:Gift:To:Recipient") ?? "To the Recipient";

    if(loading) {
        return <LoadingSegment />;
    }

    if (!(answersData?.step3Value)) {
        return <></>
    }

    return <div style={{maxWidth: '600px', margin: '0 auto'}}>
        <h5 className="mt-3">{dictionary.get("ZooPass:FormData:Adult1") ?? "1st Adult"}</h5>
        <div className="mb-3">
            <label className="form-label">{firstNameLabel}</label>
            <input type="text" aria-label={firstNameLabel} value={formData.adultFName1} onChange={e => updateFormData({ ...formData, adultFName1: e.target.value })} className={validationMessages.adultFName1 ? "form-control is-invalid" : "form-control is-valid"} required />
            { validationMessages.adultFName1 && <div className="invalid-feedback">{validationMessages.adultFName1}</div> }
        </div>
        <div className="mb-3">
            <label className="form-label">{lastNameLabel}</label>
            <input type="text" aria-label={lastNameLabel} value={formData.adultLName1} onChange={e => updateFormData({ ...formData, adultLName1: e.target.value })} className={validationMessages.adultLName1 ? "form-control is-invalid" : "form-control is-valid"} required />
            { validationMessages.adultLName1 && <div className="invalid-feedback">{validationMessages.adultLName1}</div> }
        </div>
        {(answersData?.step3Value == 2 || override == true) && <div>
            <h5 className="mt-3">{dictionary.get("ZooPass:FormData:Adult2") ?? "2nd Adult"}</h5>
            <div className="mb-3">
                <label className="form-label">{firstNameLabel}</label>
                <input type="text" aria-label={firstNameLabel} value={formData.adultFName2 || ""} onChange={e => updateFormData({ ...formData, adultFName2: e.target.value })} className={validationMessages.adultFName2 ? "form-control is-invalid" : "form-control is-valid"} required />
                { validationMessages.adultFName2 && <div className="invalid-feedback">{validationMessages.adultFName2}</div> }
            </div>
            <div className="mb-3">
                <label className="form-label">{lastNameLabel}</label>
                <input type="text" aria-label={lastNameLabel} value={formData.adultLName2 || ""} onChange={e => updateFormData({ ...formData, adultLName2: e.target.value })} className={validationMessages.adultLName2 ? "form-control is-invalid" : "form-control is-valid"} required />
                { validationMessages.adultLName2 && <div className="invalid-feedback">{validationMessages.adultLName2}</div> }
            </div>
        </div>}
        {/* Only show gift options if this is a gift membership */}
        {answersData?.step1Value == 1 && <>
            <div className="mb-3">
                <h5 className="mt-3">{dictionary.get("ZooPass:FormData:Gift:SendTo") ?? "Send Gift To"}</h5>
                <div className="form-check form-check-inline">
                    <input className="form-check-input" 
                    checked={formData.giftPackageTo == "To me"} 
                    onChange={e => updateFormData({ ...formData, giftPackageTo: "To me" })} 
                    type="radio" 
                    value="To me" 
                    aria-label={toMeLabel} />
                    <label className="form-check-label" onClick={() => updateFormData({ ...formData, giftPackageTo: "To me" })} htmlFor="inlineRadio1">{toMeLabel}</label>
                </div>
                <div className="form-check form-check-inline">
                    <input className="form-check-input" 
                    checked={formData.giftPackageTo == "To the Recipient"} 
                    onChange={e => updateFormData({ ...formData, giftPackageTo: "To the Recipient" })}
                    type="radio" 
                    value="To the Recipient" 
                    aria-label={toRecipientLabel} />
                    <label className="form-check-label" onClick={() => updateFormData({ ...formData, giftPackageTo: "To the Recipient" })} htmlFor="inlineRadio2">{toRecipientLabel}</label>
                </div>
            </div>

            <div className="mb-3">
                <h5 className="mt-3">Send Gift Renewal Notice To</h5>
                <div className="form-check form-check-inline">
                    <input className="form-check-input" 
                    checked={formData.giftRenewalTo == "To me"} 
                    onChange={e => updateFormData({ ...formData, giftRenewalTo: "To me" })} 
                    type="radio" 
                    value="To me" 
                    aria-label={toMeLabel} />
                    <label className="form-check-label" onClick={() => updateFormData({ ...formData, giftRenewalTo: "To me" })} htmlFor="inlineRadio1">{toMeLabel}</label>
                </div>
                <div className="form-check form-check-inline">
                    <input className="form-check-input" 
                    checked={formData.giftRenewalTo == "To the Recipient"} 
                    onChange={e => updateFormData({ ...formData, giftRenewalTo: "To the Recipient" })} 
                    type="radio" 
                    value="To the Recipient" 
                    aria-label={toRecipientLabel} />
                    <label className="form-check-label" onClick={() => updateFormData({ ...formData, giftRenewalTo: "To the Recipient" })} htmlFor="inlineRadio2">{toRecipientLabel}</label>
                </div>
            </div>

        
            <h5 className="mt-3">{dictionary.get("ZooPass:FormData:Gift:RecipientInformation") ?? "Recipient Information"}</h5>
            <div className="mb-3">
                <label className="form-label">{dictionary.get("ZooPass:FormData:Gift:RecipientAddress") ?? "Recipient Address"}</label>
                <input type="text" aria-label={dictionary.get("ZooPass:FormData:Gift:RecipientAddress") ?? "Recipient Address"} value={formData.recipAddress} onChange={e => updateFormData({ ...formData, recipAddress: e.target.value })} className={validationMessages.recipAddress ? "form-control is-invalid" : "form-control is-valid"} required />
                { validationMessages.recipAddress && <div className="invalid-feedback">{validationMessages.recipAddress}</div> }
            </div>

            <div className="row mb-3">
                <div className="col-md-4">
                    <label className="form-label">{dictionary.get("ZooPass:FormData:Gift:RecipientCity") ?? "Recipient City"}</label>
                    <input type="text" aria-label={dictionary.get("ZooPass:FormData:Gift:RecipientCity") ?? "Recipient City"} value={formData.recipCity} onChange={e => updateFormData({ ...formData, recipCity: e.target.value })} className={validationMessages.recipCity ? "form-control is-invalid" : "form-control is-valid"} required />
                    { validationMessages.recipCity && <div className="invalid-feedback">{validationMessages.recipCity}</div> }
                </div>
                <div className="col-md-4">
                    <label className="form-label">{dictionary.get("ZooPass:FormData:Gift:RecipientState") ?? "Recipient State"}</label>
                    <select aria-label={dictionary.get("ZooPass:FormData:Gift:RecipientState") ?? "Recipient State"} value={formData.recipState} onChange={e => updateFormData({ ...formData, recipState: e.target.value })} className={validationMessages.recipState ? "form-control is-invalid" : "form-control is-valid"} required>
                        <option value="">(Select State)</option>
                        {country && country.states.map(state => (
                            <option key={state.stateCode} value={state.stateCode}>
                                {state.description}
                            </option>
                        ))}
                    </select>
                    { validationMessages.recipState && <div className="invalid-feedback">{validationMessages.recipState}</div> }
                </div>
                <div className="col-md-4">
                    <label className="form-label">{dictionary.get("ZooPass:FormData:Gift:RecipientZip") ?? "Recipient Zip"}</label>
                    <input type="text" aria-label={dictionary.get("ZooPass:FormData:Gift:RecipientZip") ?? "Recipient Zip"} value={formData.recipPostal} onChange={e => updateFormData({ ...formData, recipPostal: e.target.value })} className={validationMessages.recipPostal ? "form-control is-invalid" : "form-control is-valid"} required />
                    { validationMessages.recipPostal && <div className="invalid-feedback">{validationMessages.recipPostal}</div> }
                </div>
            </div>

            <div className="mb-3">
                <label className="form-label">{dictionary.get("ZooPass:FormData:Gift:RecipientPhone") ?? "Recipient Phone"}</label>
                <input type="text" aria-label={dictionary.get("ZooPass:FormData:Gift:RecipientPhone") ?? "Recipient Phone"} value={formData.recipPhone} onChange={e => updateFormData({ ...formData, recipPhone: e.target.value })} className={validationMessages.recipPhone ? "form-control is-invalid" : "form-control"} />
                { validationMessages.recipPhone && <div className="invalid-feedback">{validationMessages.recipPhone}</div> }
            </div>

            <div className="mb-3">
                <label className="form-label">{dictionary.get("ZooPass:FormData:Gift:RecipientEmail") ?? "Recipient Email"}</label>
                <input type="email" aria-label={dictionary.get("ZooPass:FormData:Gift:RecipientEmail") ?? "Recipient Email"} value={formData.recipEmail} onChange={e => updateFormData({ ...formData, recipEmail: e.target.value })} className={validationMessages.recipEmail ? "form-control is-invalid" : "form-control"} />
                { validationMessages.recipEmail && <div className="invalid-feedback">{validationMessages.recipEmail}</div> }
            </div>
        
            <div>
                <h5 className="mt-3">{dictionary.get("ZooPass:FormData:Gift:GiftMessage") ?? "Gift Message"}</h5>
                <textarea aria-label={dictionary.get("ZooPass:FormData:Gift:GiftMessage") ?? "Gift Message"} value={formData.giftMessage} onChange={e => updateFormData({ ...formData, giftMessage: e.target.value })} className="form-control" />
            </div>
        </>}
    </div>;
};